import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import ComboFiltro from '../../../../components/ComboFiltro';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity, formatCurrency } from '../../../../utils/formatters';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './NuovoSal.api';
import './NuovoSal.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

interface SorgenteCommessa {
  id: number;
  numero: string;
  descrizione: string | null;
  clienteRagioneSociale: string | null;
  societaId: number;
}

interface SorgenteRiga {
  rigaOrdineId: number;
  ordinePos: number;
  tipoRiga: string;
  descrizione: string;
  unitaMisura: string | null;
  quantitaTotale: number | null;
  quantitaPrecedente: number;
  residua: number | null;
  prezzoUnitario: number;
  importoNetto: number | null;
}

interface SorgenteOrdine {
  id: number;
  numero: string;
  stato: string;
  destinatarioOrdine: string | null;
  dataConferma: string | null;
  totale: number | null;
  righe: SorgenteRiga[];
}

interface SorgenteExtra {
  rigaDistintaExtraId: number;
  distintaEtichetta: string;
  distintaTipo: 'MERCE' | 'LAVORAZIONI';
  descrizione: string;
  unitaMisura: string | null;
  quantitaTotale: number;
  quantitaPrecedente: number;
  residua: number;
}

interface SorgenteAcconto {
  proformaAccontoId: number;
  numero: string;
  totale: number;
  dataPagamento: string | null;
  ordineId: number | null;
  stornato: number;
  residuoStornabile: number;
}

// Righe in editing locale (quantità/prezzi come stringhe con virgola)
interface RigaLocale {
  rigaOrdineId: number;
  ordinePos: number;
  tipoRiga: string;
  descrizione: string;
  unitaMisura: string | null;
  quantitaTotale: number | null;
  quantitaPrecedente: number;
  residua: number | null;
  prezzoUnitario: number;
  selezionata: boolean;
  qtaCorrente: string;
}

interface OrdineLocale {
  id: number;
  numero: string;
  stato: string;
  aperto: boolean;
  righe: RigaLocale[];
}

interface ExtraLocale {
  rigaDistintaExtraId: number;
  distintaEtichetta: string;
  distintaTipo: 'MERCE' | 'LAVORAZIONI';
  descrizione: string;
  unitaMisura: string | null;
  quantitaTotale: number;
  quantitaPrecedente: number;
  residua: number;
  selezionata: boolean;
  qtaCorrente: string;
  prezzo: string;
}

// Riga aggiunta a mano: integrazioni o lavorazioni extra fuori dagli ordini
interface RigaLiberaLocale {
  key: string;
  descrizione: string;
  qtaCorrente: string;
  prezzo: string;
}

interface AccontoLocale {
  proformaAccontoId: number;
  numero: string;
  totale: number;
  dataPagamento: string | null;
  stornato: number;
  residuoStornabile: number;
  selezionato: boolean;
  importo: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Converte una stringa con virgola decimale in number
function toNum(s: string): number {
  return Number(s.replace(',', '.'));
}

// Da number a stringa input con virgola
function numToInput(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return String(Number(n)).replace('.', ',');
}

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

// Importo live di una riga ordine selezionata (null se non calcolabile)
function importoRiga(r: RigaLocale): number | null {
  if (!r.selezionata) return null;
  const q = toNum(r.qtaCorrente);
  if (!Number.isFinite(q)) return null;
  return Math.round(q * r.prezzoUnitario * 100) / 100;
}

// Importo live di una voce extra selezionata (null se non calcolabile)
function importoExtra(x: ExtraLocale): number | null {
  if (!x.selezionata) return null;
  const q = toNum(x.qtaCorrente);
  const p = toNum(x.prezzo);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return null;
  return Math.round(q * p * 100) / 100;
}

// Importo live di una riga libera (null se non calcolabile)
function importoLibera(x: RigaLiberaLocale): number | null {
  const q = toNum(x.qtaCorrente);
  const p = toNum(x.prezzo);
  if (!Number.isFinite(q) || !Number.isFinite(p) || x.qtaCorrente.trim() === '' || x.prezzo.trim() === '') return null;
  return Math.round(q * p * 100) / 100;
}

// Riga d'ordine con residuo portata a saldo: selezionata con tutta la quantità residua
function rigaASaldo(r: RigaLocale): RigaLocale {
  if (r.residua === null || r.residua <= 0) return r;
  return { ...r, selezionata: true, qtaCorrente: numToInput(r.residua) };
}

let contatoreRigaLibera = 0;

const STATO_ORDINE_CFG: Record<string, { label: string; cls: string }> = {
  BOZZA:          { label: 'Bozza',          cls: 'bop-badge-neutral' },
  CONFERMATO:     { label: 'Confermato',     cls: 'bop-badge-info' },
  IN_LAVORAZIONE: { label: 'In lavorazione', cls: 'bop-badge-warning' },
  CHIUSO:         { label: 'Chiuso',         cls: 'bop-badge-success' },
};

function BadgeStatoOrdine({ stato }: { stato: string }) {
  const cfg = STATO_ORDINE_CFG[stato] ?? { label: stato, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function BadgeTipoDistinta({ tipo }: { tipo: 'MERCE' | 'LAVORAZIONI' }) {
  return tipo === 'MERCE'
    ? <span className="bop-badge bop-badge-info">Merce</span>
    : <span className="bop-badge bop-badge-warning">Lavorazioni</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function NuovoSal() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const commessaIdParam = searchParams.get('commessaId');
  const ordineIdParam = searchParams.get('ordineId');
  const commessaId = commessaIdParam ? parseInt(commessaIdParam, 10) : null;
  // ordineId serve SOLO come preselezione dell'accordion — la sorgente carica tutta la commessa
  const ordineIdPresel = ordineIdParam ? parseInt(ordineIdParam, 10) : null;

  // ── Step scelta commessa (quando commessaId assente) ────────────────────────
  const { societaId, setSocietaId, listaSocieta, isDisabled } = useFiltroSocieta();
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);
  const [commessaSel, setCommessaSel] = useState('');

  useEffect(() => {
    if (commessaId || !societaId) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaId}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [commessaId, societaId]);

  // Resetta la commessa selezionata se cambia società
  useEffect(() => { setCommessaSel(''); }, [societaId]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim() + (c.clienteRagioneSociale ? ` (${c.clienteRagioneSociale})` : ''),
  }));

  const caricaRigheCommessa = () => {
    if (!commessaSel) return;
    const next = new URLSearchParams(searchParams);
    next.set('commessaId', commessaSel);
    next.delete('ordineId');
    setSearchParams(next);
  };

  // ── Stato pagina con commessa caricata ──────────────────────────────────────
  const [loading, setLoading] = useState(!!commessaId);
  const [errore, setErrore] = useState<string | null>(null);
  const [commessa, setCommessa] = useState<SorgenteCommessa | null>(null);

  const [ordini, setOrdini] = useState<OrdineLocale[]>([]);
  const [extra, setExtra] = useState<ExtraLocale[]>([]);
  const [acconti, setAcconti] = useState<AccontoLocale[]>([]);
  const [righeLibere, setRigheLibere] = useState<RigaLiberaLocale[]>([]);
  const [snapshot, setSnapshot] = useState('');

  const [salvando, setSalvando] = useState(false);

  // Snapshot normalizzato: esclude il flag "aperto" degli accordion
  const statoCorrente = useMemo(
    () => JSON.stringify({ righe: ordini.map(o => o.righe), extra, acconti, righeLibere }),
    [ordini, extra, acconti, righeLibere]
  );
  const isDirty = snapshot !== '' && statoCorrente !== snapshot;
  useDirtyForm(isDirty, commessa ? `Nuovo SAL — Commessa ${commessa.numero}` : 'Nuovo SAL');

  // ── Caricamento sorgente (tutta la commessa, mai filtrata per ordine) ───────

  const carica = useCallback(async () => {
    if (!commessaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/sal/sorgente?commessaId -> {"success":true,"data":{"commessa":{"id":"number","numero":"string","descrizione":"string|null","clienteRagioneSociale":"string|null","societaId":"number"},"ordini":[{"id":"number","numero":"string","stato":"string","destinatarioOrdine":"string|null","dataConferma":"string|null","totale":"number|null","righe":[{"rigaOrdineId":"number","ordinePos":"number","tipoRiga":"string","descrizione":"string","unitaMisura":"string|null","quantitaTotale":"number|null","quantitaPrecedente":"number","residua":"number|null","prezzoUnitario":"number","importoNetto":"number|null"}]}],"extra":[{"rigaDistintaExtraId":"number","distintaEtichetta":"string","distintaTipo":"MERCE|LAVORAZIONI","descrizione":"string","unitaMisura":"string|null","quantitaTotale":"number","quantitaPrecedente":"number","residua":"number"}],"acconti":[{"proformaAccontoId":"number","numero":"string","totale":"number","dataPagamento":"string|null","ordineId":"number|null","stornato":"number","residuoStornabile":"number"}]}}
      const r = await apiFetch<{
        success: boolean;
        data?: {
          commessa: SorgenteCommessa;
          ordini: SorgenteOrdine[];
          extra: SorgenteExtra[];
          acconti: SorgenteAcconto[];
        };
        error?: unknown;
      }>(`/api/sal/sorgente?commessaId=${commessaId}`);
      if (!r.success || !r.data) {
        setErrore(estraiErrore({ payload: r }, 'Errore nel caricamento delle righe della commessa'));
        setCommessa(null);
        return;
      }
      setCommessa(r.data.commessa);

      const ordiniSorgente = r.data.ordini || [];
      const ordiniLocali: OrdineLocale[] = ordiniSorgente.map(o => ({
        id: o.id,
        numero: o.numero,
        stato: o.stato,
        // Aperto di default se è l'ordine preselezionato o se è l'unico
        aperto: (ordineIdPresel !== null && o.id === ordineIdPresel) || ordiniSorgente.length === 1,
        righe: (o.righe || []).map(rr => {
          const residua = rr.residua !== null && rr.residua !== undefined ? Number(rr.residua) : null;
          return {
            rigaOrdineId: rr.rigaOrdineId,
            ordinePos: rr.ordinePos,
            tipoRiga: rr.tipoRiga,
            descrizione: rr.descrizione,
            unitaMisura: rr.unitaMisura,
            quantitaTotale: rr.quantitaTotale !== null && rr.quantitaTotale !== undefined ? Number(rr.quantitaTotale) : null,
            quantitaPrecedente: Number(rr.quantitaPrecedente ?? 0),
            residua,
            prezzoUnitario: Number(rr.prezzoUnitario ?? 0),
            selezionata: false,
            qtaCorrente: residua !== null && residua > 0 ? numToInput(residua) : '',
          };
        }),
      }));
      setOrdini(ordiniLocali);

      const extraLocali: ExtraLocale[] = (r.data.extra || []).map(ex => {
        const residua = Number(ex.residua ?? 0);
        return {
          rigaDistintaExtraId: ex.rigaDistintaExtraId,
          distintaEtichetta: ex.distintaEtichetta,
          distintaTipo: ex.distintaTipo,
          descrizione: ex.descrizione,
          unitaMisura: ex.unitaMisura,
          quantitaTotale: Number(ex.quantitaTotale ?? 0),
          quantitaPrecedente: Number(ex.quantitaPrecedente ?? 0),
          residua,
          selezionata: false,
          qtaCorrente: residua > 0 ? numToInput(residua) : '',
          prezzo: '0',
        };
      });
      setExtra(extraLocali);

      const accontiLocali: AccontoLocale[] = (r.data.acconti || []).map(a => ({
        proformaAccontoId: a.proformaAccontoId,
        numero: a.numero,
        totale: Number(a.totale ?? 0),
        dataPagamento: a.dataPagamento,
        stornato: Number(a.stornato ?? 0),
        residuoStornabile: Number(a.residuoStornabile ?? 0),
        selezionato: false,
        importo: '',
      }));
      setAcconti(accontiLocali);
      setRigheLibere([]);

      setSnapshot(JSON.stringify({
        righe: ordiniLocali.map(o => o.righe),
        extra: extraLocali,
        acconti: accontiLocali,
        righeLibere: [],
      }));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento delle righe della commessa'));
      setCommessa(null);
    } finally {
      setLoading(false);
    }
  }, [commessaId, ordineIdPresel]);

  useEffect(() => { carica(); }, [carica]);

  // ── Editing righe ordine ────────────────────────────────────────────────────

  const toggleOrdineAperto = (ordineId: number) => {
    setOrdini(prev => prev.map(o => (o.id === ordineId ? { ...o, aperto: !o.aperto } : o)));
  };

  const toggleRiga = (rigaOrdineId: number) => {
    setOrdini(prev => prev.map(o => ({
      ...o,
      righe: o.righe.map(r => {
        if (r.rigaOrdineId !== rigaOrdineId) return r;
        const selezionata = !r.selezionata;
        return {
          ...r,
          selezionata,
          qtaCorrente: selezionata && !r.qtaCorrente && r.residua !== null && r.residua > 0
            ? numToInput(r.residua)
            : r.qtaCorrente,
        };
      }),
    })));
  };

  const setQtaRiga = (rigaOrdineId: number, val: string) => {
    setOrdini(prev => prev.map(o => ({
      ...o,
      righe: o.righe.map(r => (r.rigaOrdineId === rigaOrdineId ? { ...r, qtaCorrente: val } : r)),
    })));
  };

  // Evadi a saldo: seleziona tutte le righe con residuo, ciascuna con l'intera
  // quantità residua (di un solo ordine se ordineId è indicato)
  const evadiASaldo = (ordineId?: number) => {
    const daPortare = ordini
      .filter(o => ordineId === undefined || o.id === ordineId)
      .reduce((n, o) => n + o.righe.filter(r => r.residua !== null && r.residua > 0).length, 0);
    if (daPortare === 0) {
      showToast('Nessuna riga con quantità residua da portare a saldo', 'info');
      return;
    }
    setOrdini(prev => prev.map(o => (ordineId === undefined || o.id === ordineId)
      ? { ...o, aperto: true, righe: o.righe.map(rigaASaldo) }
      : o));
  };

  // ── Righe aggiuntive (integrazioni / lavorazioni extra) ─────────────────────

  const aggiungiRigaLibera = () => {
    contatoreRigaLibera += 1;
    setRigheLibere(prev => [...prev, { key: `lib-${contatoreRigaLibera}`, descrizione: '', qtaCorrente: '1', prezzo: '' }]);
  };

  const setCampoLibera = (key: string, campo: 'descrizione' | 'qtaCorrente' | 'prezzo', val: string) => {
    setRigheLibere(prev => prev.map(x => (x.key === key ? { ...x, [campo]: val } : x)));
  };

  const rimuoviRigaLibera = (key: string) => {
    setRigheLibere(prev => prev.filter(x => x.key !== key));
  };

  // ── Editing voci extra ──────────────────────────────────────────────────────

  const toggleExtra = (rigaDistintaExtraId: number) => {
    setExtra(prev => prev.map(x => {
      if (x.rigaDistintaExtraId !== rigaDistintaExtraId) return x;
      const selezionata = !x.selezionata;
      return {
        ...x,
        selezionata,
        qtaCorrente: selezionata && !x.qtaCorrente && x.residua > 0 ? numToInput(x.residua) : x.qtaCorrente,
      };
    }));
  };

  const setCampoExtra = (rigaDistintaExtraId: number, campo: 'qtaCorrente' | 'prezzo', val: string) => {
    setExtra(prev => prev.map(x => (x.rigaDistintaExtraId === rigaDistintaExtraId ? { ...x, [campo]: val } : x)));
  };

  // ── Importi live ────────────────────────────────────────────────────────────

  const totaleRighe = useMemo(() => {
    let tot = 0;
    for (const o of ordini) {
      for (const r of o.righe) {
        const imp = importoRiga(r);
        if (imp !== null) tot += imp;
      }
    }
    for (const x of extra) {
      const imp = importoExtra(x);
      if (imp !== null) tot += imp;
    }
    for (const x of righeLibere) {
      const imp = importoLibera(x);
      if (imp !== null) tot += imp;
    }
    return Math.round(tot * 100) / 100;
  }, [ordini, extra, righeLibere]);

  const totaleStorni = useMemo(() => {
    let tot = 0;
    for (const a of acconti) {
      if (!a.selezionato) continue;
      const imp = toNum(a.importo);
      if (Number.isFinite(imp)) tot += imp;
    }
    return Math.round(tot * 100) / 100;
  }, [acconti]);

  const nettoSal = Math.round((totaleRighe - totaleStorni) * 100) / 100;

  const nSelezionate =
    ordini.reduce((n, o) => n + o.righe.filter(r => r.selezionata).length, 0) +
    extra.filter(x => x.selezionata).length +
    righeLibere.length;

  const nRigheConResiduo = ordini.reduce(
    (n, o) => n + o.righe.filter(r => r.residua !== null && r.residua > 0).length, 0
  );

  // ── Editing acconti ─────────────────────────────────────────────────────────

  const toggleAcconto = (proformaAccontoId: number) => {
    setAcconti(prev => prev.map(a => {
      if (a.proformaAccontoId !== proformaAccontoId) return a;
      const selezionato = !a.selezionato;
      // Default importo da stornare = min(residuo stornabile, totale righe correnti)
      const importoDefault = Math.max(0, Math.min(a.residuoStornabile, totaleRighe));
      return {
        ...a,
        selezionato,
        importo: selezionato && !a.importo ? numToInput(Math.round(importoDefault * 100) / 100) : a.importo,
      };
    }));
  };

  const setImportoAcconto = (proformaAccontoId: number, val: string) => {
    setAcconti(prev => prev.map(a => (a.proformaAccontoId === proformaAccontoId ? { ...a, importo: val } : a)));
  };

  // ── Creazione ───────────────────────────────────────────────────────────────

  const creaSal = async () => {
    if (!commessaId) return;

    const payloadRighe: Array<{
      rigaOrdineId?: number;
      rigaDistintaExtraId?: number;
      descrizione: string;
      quantitaTotale: number | null;
      quantitaPrecedente: number;
      quantitaCorrente: number;
      prezzoUnitario: number;
      importoCorrente: number;
    }> = [];

    for (const o of ordini) {
      for (const r of o.righe) {
        if (!r.selezionata) continue;
        const q = toNum(r.qtaCorrente);
        if (!Number.isFinite(q) || q <= 0) {
          showToast(`Quantità corrente non valida per la riga "${r.descrizione}" (ordine ${o.numero})`, 'error');
          return;
        }
        payloadRighe.push({
          rigaOrdineId: r.rigaOrdineId,
          descrizione: r.descrizione,
          quantitaTotale: r.quantitaTotale,
          quantitaPrecedente: r.quantitaPrecedente,
          quantitaCorrente: q,
          prezzoUnitario: r.prezzoUnitario,
          importoCorrente: Math.round(q * r.prezzoUnitario * 100) / 100,
        });
      }
    }

    for (const x of extra) {
      if (!x.selezionata) continue;
      const q = toNum(x.qtaCorrente);
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità corrente non valida per la voce extra "${x.descrizione}"`, 'error');
        return;
      }
      const p = toNum(x.prezzo);
      if (!Number.isFinite(p) || p < 0) {
        showToast(`Prezzo unitario non valido per la voce extra "${x.descrizione}"`, 'error');
        return;
      }
      payloadRighe.push({
        rigaDistintaExtraId: x.rigaDistintaExtraId,
        descrizione: x.descrizione,
        quantitaTotale: x.quantitaTotale,
        quantitaPrecedente: x.quantitaPrecedente,
        quantitaCorrente: q,
        prezzoUnitario: p,
        importoCorrente: Math.round(q * p * 100) / 100,
      });
    }

    for (const x of righeLibere) {
      const descrizione = x.descrizione.trim();
      if (!descrizione) {
        showToast('Indica la descrizione di ogni riga aggiuntiva', 'error');
        return;
      }
      const q = toNum(x.qtaCorrente);
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per la riga aggiuntiva "${descrizione}"`, 'error');
        return;
      }
      const p = toNum(x.prezzo);
      if (x.prezzo.trim() === '' || !Number.isFinite(p) || p < 0) {
        showToast(`Prezzo unitario non valido per la riga aggiuntiva "${descrizione}"`, 'error');
        return;
      }
      payloadRighe.push({
        descrizione,
        quantitaTotale: q,
        quantitaPrecedente: 0,
        quantitaCorrente: q,
        prezzoUnitario: p,
        importoCorrente: Math.round(q * p * 100) / 100,
      });
    }

    if (payloadRighe.length === 0) {
      showToast('Seleziona almeno una riga da portare a SAL', 'error');
      return;
    }

    const payloadStorni: Array<{ proformaAccontoId: number; importoStornato: number }> = [];
    for (const a of acconti) {
      if (!a.selezionato) continue;
      const imp = toNum(a.importo);
      if (!Number.isFinite(imp) || imp <= 0) {
        showToast(`Importo da stornare non valido per la proforma ${a.numero}`, 'error');
        return;
      }
      if (imp > a.residuoStornabile) {
        showToast(`L'importo da stornare per la proforma ${a.numero} supera il residuo stornabile (${formatCurrency(a.residuoStornabile)})`, 'error');
        return;
      }
      payloadStorni.push({ proformaAccontoId: a.proformaAccontoId, importoStornato: imp });
    }

    setSalvando(true);
    try {
      // @api POST /api/sal {"commessaId":"number","righe":[{"rigaOrdineId":"number?","rigaDistintaExtraId":"number?","descrizione":"string","quantitaTotale":"number|null","quantitaPrecedente":"number","quantitaCorrente":"number","prezzoUnitario":"number","importoCorrente":"number"}],"storni":[{"proformaAccontoId":"number","importoStornato":"number"}]} -> {"success":true,"data":{"id":"number"}}
      const res = await apiFetch<{ success: boolean; data?: { id: number }; error?: unknown }>('/api/sal', {
        method: 'POST',
        body: JSON.stringify({ commessaId, righe: payloadRighe, storni: payloadStorni }),
      });
      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella creazione del SAL'), 'error');
        return;
      }
      showToast('SAL creato in bozza', 'success');
      setSnapshot(statoCorrente);
      navigate(`/commerciale/attivo/sal/${res.data.id}`);
    } catch (err: unknown) {
      // 409 SOVRA_EVASIONE | STORNO_NON_VALIDO: message dettagliato dal backend
      showToast(estraiErrore(err, 'Errore nella creazione del SAL'), 'error');
    } finally {
      setSalvando(false);
    }
  };

  // ── Render: step scelta commessa ────────────────────────────────────────────

  if (!commessaId) {
    return (
      <Layout>
        <div className="sal-nuovo">
          <BopBackLink to="/commerciale/attivo/sal" label="Torna ai SAL" />

          <div className="bop-page-header">
            <h1 className="bop-page-title">Nuovo SAL</h1>
          </div>

          <div className="bop-card sal-nuovo__scelta-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Seleziona la commessa</h2>
            </div>
            <div className="bop-card__body">
              <p className="sal-nuovo__scelta-hint">
                Il SAL copre le righe di tutti gli ordini della commessa, le voci extra a carico
                cliente e lo storno degli acconti pagati.
              </p>
              <div className="sal-nuovo__scelta-row">
                {!isDisabled && (
                  <div className="bop-form-group sal-nuovo__scelta-societa">
                    <label className="bop-label">Società</label>
                    <select
                      className="bop-select"
                      value={societaId}
                      onChange={e => setSocietaId(e.target.value)}
                    >
                      <option value="">Seleziona società…</option>
                      {listaSocieta.map(s => (
                        <option key={s.id} value={String(s.id)}>{s.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="bop-form-group sal-nuovo__scelta-combo">
                  <label className="bop-label">Commessa</label>
                  <ComboFiltro
                    label="commessa"
                    value={commessaSel || null}
                    onChange={id => setCommessaSel(id || '')}
                    items={commesseComboItems}
                    disabled={!societaId}
                  />
                </div>
                <button
                  className="bop-btn bop-btn-primary sal-nuovo__scelta-btn"
                  onClick={caricaRigheCommessa}
                  disabled={!commessaSel}
                >
                  Carica righe
                </button>
              </div>
            </div>
          </div>

          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  // ── Render: caricamento / errore ────────────────────────────────────────────
  // Nota: (!errore && !commessa) copre il render tra setSearchParams e l'avvio
  // dell'effetto di caricamento, evitando il flash della schermata di errore.

  if (loading || (!errore && !commessa)) {
    return (
      <Layout>
        <div className="sal-nuovo">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento righe della commessa…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !commessa) {
    return (
      <Layout>
        <div className="sal-nuovo">
          <BopBackLink to="/commerciale/attivo/sal" label="Torna ai SAL" />
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Impossibile creare il SAL</h3>
              <p className="bop-empty-state__text">{errore || 'Commessa non disponibile.'}</p>
            </div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  // ── Render: pagina con sorgente caricata ────────────────────────────────────

  return (
    <Layout>
      <div className="sal-nuovo">
        <BopBackLink to="/commerciale/attivo/sal" label="Torna ai SAL" />

        {/* Intestazione commessa */}
        <div className="bop-page-header">
          <div className="sal-nuovo__intestazione">
            <h1 className="bop-page-title">
              Nuovo SAL — Commessa {commessa.numero}
              {commessa.descrizione ? ` — ${commessa.descrizione}` : ''}
            </h1>
            {commessa.clienteRagioneSociale && (
              <span className="sal-nuovo__cliente">Cliente: {commessa.clienteRagioneSociale}</span>
            )}
          </div>
        </div>

        {/* Righe ordine — un accordion per ogni ordine */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe ordine</h2>
            {ordini.length > 0 && (
              <button
                type="button"
                className="bop-btn bop-btn-secondary bop-btn-sm"
                onClick={() => evadiASaldo()}
                disabled={nRigheConResiduo === 0}
                title="Seleziona tutte le righe con residuo, ciascuna con l'intera quantità residua"
              >
                Evadi a saldo
              </button>
            )}
          </div>
          <div className="bop-card__body">
            {ordini.length === 0 ? (
              <div className="bop-empty-state">Nessun ordine con righe disponibili per il SAL.</div>
            ) : (
              ordini.map(o => {
                const nSelezionabili = o.righe.filter(r => r.residua === null || r.residua > 0).length;
                return (
                  <div className="sal-nuovo__accordion" key={o.id}>
                    <button
                      type="button"
                      className="sal-nuovo__accordion-header"
                      onClick={() => toggleOrdineAperto(o.id)}
                    >
                      <span className={`sal-nuovo__accordion-arrow ${o.aperto ? 'sal-nuovo__accordion-arrow--open' : ''}`}>
                        ▸
                      </span>
                      <span className="sal-nuovo__accordion-title">Ordine {o.numero}</span>
                      <BadgeStatoOrdine stato={o.stato} />
                      <span className="sal-nuovo__accordion-count">
                        {nSelezionabili} {nSelezionabili === 1 ? 'riga selezionabile' : 'righe selezionabili'}
                      </span>
                    </button>
                    {o.aperto && (
                      <div className="sal-nuovo__accordion-body">
                        {nSelezionabili > 0 && (
                          <div className="bop-sal-saldo-bar">
                            <button
                              type="button"
                              className="bop-btn bop-btn-ghost bop-btn-sm"
                              onClick={() => evadiASaldo(o.id)}
                            >
                              Evadi a saldo l'ordine {o.numero}
                            </button>
                          </div>
                        )}
                        {o.righe.length === 0 ? (
                          <div className="bop-empty-state">Nessuna riga in questo ordine.</div>
                        ) : (
                          <table className="bop-table">
                            <thead>
                              <tr>
                                <th className="bop-table__th--select"></th>
                                <th>Pos</th>
                                <th>Tipo</th>
                                <th>Descrizione</th>
                                <th>U.M.</th>
                                <th className="bop-text-right">Q.tà totale</th>
                                <th className="bop-text-right">Già a SAL</th>
                                <th className="bop-text-right">Residua</th>
                                <th className="bop-text-right">Prezzo unit.</th>
                                <th className="bop-text-right">Q.tà corrente</th>
                                <th className="bop-text-right">Importo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.righe.map(r => {
                                const evasa = r.residua !== null && r.residua <= 0;
                                const imp = importoRiga(r);
                                return (
                                  <tr key={r.rigaOrdineId} className={evasa ? 'sal-nuovo__row--evasa' : ''}>
                                    <td className="bop-table__td--select">
                                      <input
                                        type="checkbox"
                                        checked={r.selezionata}
                                        disabled={evasa}
                                        onChange={() => toggleRiga(r.rigaOrdineId)}
                                      />
                                    </td>
                                    <td>{r.ordinePos}</td>
                                    <td>{r.tipoRiga || <span className="bop-text-muted">—</span>}</td>
                                    <td>
                                      {r.descrizione}
                                      {evasa && (
                                        <span className="bop-badge bop-badge-neutral sal-nuovo__badge-evasa">Evasa</span>
                                      )}
                                    </td>
                                    <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                                    <td className="bop-text-right">
                                      {r.quantitaTotale !== null ? formatQuantity(r.quantitaTotale) : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td className="bop-text-right">{formatQuantity(r.quantitaPrecedente)}</td>
                                    <td className="bop-text-right">
                                      {r.residua !== null ? formatQuantity(r.residua) : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td className="bop-text-right">{formatCurrency(r.prezzoUnitario)}</td>
                                    <td className="bop-text-right">
                                      <input
                                        className="bop-input sal-nuovo__qta-input"
                                        value={r.selezionata ? r.qtaCorrente : ''}
                                        disabled={!r.selezionata}
                                        onChange={e => setQtaRiga(r.rigaOrdineId, e.target.value)}
                                      />
                                    </td>
                                    <td className="bop-text-right">
                                      {imp !== null ? formatCurrency(imp) : <span className="bop-text-muted">—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Voci extra a carico cliente (da distinte) */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Voci extra a carico cliente (da distinte)</h2>
          </div>
          <div className="bop-card__body">
            {extra.length === 0 ? (
              <div className="bop-empty-state">Nessuna voce extra a carico cliente.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th className="bop-table__th--select"></th>
                    <th>Distinta</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Q.tà</th>
                    <th className="bop-text-right">Già a SAL</th>
                    <th className="bop-text-right">Residua</th>
                    <th className="bop-text-right">Q.tà corrente</th>
                    <th className="bop-text-right">Prezzo unit.</th>
                    <th className="bop-text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {extra.map(x => {
                    const evasa = x.residua <= 0;
                    const imp = importoExtra(x);
                    return (
                      <tr key={x.rigaDistintaExtraId} className={evasa ? 'sal-nuovo__row--evasa' : ''}>
                        <td className="bop-table__td--select">
                          <input
                            type="checkbox"
                            checked={x.selezionata}
                            disabled={evasa}
                            onChange={() => toggleExtra(x.rigaDistintaExtraId)}
                          />
                        </td>
                        <td>
                          <span className="sal-nuovo__distinta">
                            {x.distintaEtichetta}
                            <BadgeTipoDistinta tipo={x.distintaTipo} />
                          </span>
                        </td>
                        <td>{x.descrizione}</td>
                        <td>{x.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                        <td className="bop-text-right">{formatQuantity(x.quantitaTotale)}</td>
                        <td className="bop-text-right">{formatQuantity(x.quantitaPrecedente)}</td>
                        <td className="bop-text-right">{formatQuantity(x.residua)}</td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input sal-nuovo__qta-input"
                            value={x.selezionata ? x.qtaCorrente : ''}
                            disabled={!x.selezionata}
                            onChange={e => setCampoExtra(x.rigaDistintaExtraId, 'qtaCorrente', e.target.value)}
                          />
                        </td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input sal-nuovo__prezzo-input"
                            value={x.selezionata ? x.prezzo : ''}
                            disabled={!x.selezionata}
                            onChange={e => setCampoExtra(x.rigaDistintaExtraId, 'prezzo', e.target.value)}
                          />
                        </td>
                        <td className="bop-text-right">
                          {imp !== null ? formatCurrency(imp) : <span className="bop-text-muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Righe aggiuntive: integrazioni o lavorazioni extra fuori dagli ordini */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe aggiuntive (integrazioni / lavorazioni extra)</h2>
            <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={aggiungiRigaLibera}>
              + Aggiungi riga
            </button>
          </div>
          <div className="bop-card__body">
            {righeLibere.length === 0 ? (
              <div className="bop-empty-state">
                Nessuna riga aggiuntiva. Usa «Aggiungi riga» per integrazioni o lavorazioni non previste dagli ordini.
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Descrizione</th>
                    <th className="bop-text-right">Q.tà</th>
                    <th className="bop-text-right">Prezzo unit.</th>
                    <th className="bop-text-right">Importo</th>
                    <th className="bop-table__th--actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {righeLibere.map(x => {
                    const imp = importoLibera(x);
                    return (
                      <tr key={x.key}>
                        <td>
                          <input
                            className="bop-input bop-sal-riga-libera__descrizione"
                            value={x.descrizione}
                            placeholder="Es. Integrazione impianto — lavorazione extra"
                            onChange={e => setCampoLibera(x.key, 'descrizione', e.target.value)}
                          />
                        </td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input sal-nuovo__qta-input"
                            value={x.qtaCorrente}
                            onChange={e => setCampoLibera(x.key, 'qtaCorrente', e.target.value)}
                          />
                        </td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input sal-nuovo__prezzo-input"
                            value={x.prezzo}
                            onChange={e => setCampoLibera(x.key, 'prezzo', e.target.value)}
                          />
                        </td>
                        <td className="bop-text-right">
                          {imp !== null ? formatCurrency(imp) : <span className="bop-text-muted">—</span>}
                        </td>
                        <td className="bop-table__td--actions">
                          <button
                            type="button"
                            className="bop-btn bop-btn-danger bop-btn-sm"
                            onClick={() => rimuoviRigaLibera(x.key)}
                          >
                            Rimuovi
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Storno acconti pagati */}
        {acconti.length > 0 && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Storno acconti pagati</h2>
            </div>
            <div className="bop-card__body">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th className="bop-table__th--select"></th>
                    <th>Proforma</th>
                    <th>Pagata il</th>
                    <th className="bop-text-right">Totale</th>
                    <th className="bop-text-right">Già stornato</th>
                    <th className="bop-text-right">Residuo stornabile</th>
                    <th className="bop-text-right">Importo da stornare</th>
                  </tr>
                </thead>
                <tbody>
                  {acconti.map(a => {
                    const stornato = a.residuoStornabile <= 0;
                    return (
                      <tr key={a.proformaAccontoId} className={stornato ? 'sal-nuovo__row--evasa' : ''}>
                        <td className="bop-table__td--select">
                          <input
                            type="checkbox"
                            checked={a.selezionato}
                            disabled={stornato}
                            onChange={() => toggleAcconto(a.proformaAccontoId)}
                          />
                        </td>
                        <td>
                          {a.numero}
                          {stornato && (
                            <span className="bop-badge bop-badge-neutral sal-nuovo__badge-evasa">Stornato</span>
                          )}
                        </td>
                        <td>{formatDateIT(a.dataPagamento)}</td>
                        <td className="bop-text-right">{formatCurrency(a.totale)}</td>
                        <td className="bop-text-right">{formatCurrency(a.stornato)}</td>
                        <td className="bop-text-right">{formatCurrency(a.residuoStornabile)}</td>
                        <td className="bop-text-right">
                          <input
                            className="bop-input sal-nuovo__prezzo-input"
                            value={a.selezionato ? a.importo : ''}
                            disabled={!a.selezionato || stornato}
                            onChange={e => setImportoAcconto(a.proformaAccontoId, e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Riepilogo sticky */}
        <div className="bop-card sal-nuovo__footer">
          <div className="bop-card__body">
            <div className="sal-nuovo__footer-row">
              <div className="sal-nuovo__riepilogo">
                <div className="sal-nuovo__riepilogo-item">
                  <span className="sal-nuovo__riepilogo-label">
                    Totale righe ({nSelezionate} {nSelezionate === 1 ? 'riga' : 'righe'})
                  </span>
                  <span className="sal-nuovo__riepilogo-value">{formatCurrency(totaleRighe)}</span>
                </div>
                <div className="sal-nuovo__riepilogo-item">
                  <span className="sal-nuovo__riepilogo-label">− Storno acconti</span>
                  <span className="sal-nuovo__riepilogo-value sal-nuovo__riepilogo-value--storno">
                    {formatCurrency(totaleStorni)}
                  </span>
                </div>
                <div className="sal-nuovo__riepilogo-item">
                  <span className="sal-nuovo__riepilogo-label">= Netto SAL</span>
                  <span className="sal-nuovo__riepilogo-value sal-nuovo__riepilogo-value--netto">
                    {formatCurrency(nettoSal)}
                  </span>
                </div>
              </div>
              <div className="sal-nuovo__footer-actions">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={() => navigate(-1)}
                  disabled={salvando}
                >
                  Annulla
                </button>
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={creaSal}
                  disabled={salvando || nSelezionate === 0}
                >
                  {salvando ? 'Creazione…' : 'Crea SAL in bozza'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
